import { IDivergence } from "@/@types/divergence";
import { ReprocessProvider } from "@/context/reprocess-context";
import { Tags } from "@/enum/tags";
import { useGetDTEFPaymentValuesQuery } from "@/services/conciliation/dtef-payment";
import { useGetDivergenceQuery } from "@/services/divergence";
import { apiSlice } from "@/services/http";
import { useGetOneStoreConciliationQuery } from "@/services/store-conciliation";
import { layoutClasses } from "@/utils/css-classes";
import { PageLayout } from "components/layout";
import { Load } from "components/load";
import { useCallback, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { useParams } from "react-router-dom";
import DivergenceList from "./components/divergence-list";
import { useFilterState } from "./components/divergence-list/ducks/filter/hooks/filterState";
import DivergenceRecorded from "./components/divergence-recorded";
import { ReprocessCompletedModal } from "./components/reprocess-completed-modal";
import { ReprocessFloatingCard } from "./components/reprocess-floating-card";
import TransactionResume from "./components/transaction-resume";
import { formatDateToBR } from "./components/transaction-resume/utils";
import { OpenCashRegistersBanner } from "./partial-operator-balance/open-cash-registers-banner";
import { useOpenCashRegisters } from "./partial-operator-balance/use-open-cash-registers";

export default function ConciliationPage() {
  const dispatch = useDispatch();
  const [isCollapsibleOpen, setIsCollapsibleOpen] = useState(false);
  const { id_transaction } = useParams<{ id_transaction: string }>();
  const conciliation_id = id_transaction;
  const { filters } = useFilterState() || { filters: {} };
  const openCashRegisters = useOpenCashRegisters(conciliation_id);

  const { data: response, isLoading, isFetching } = useGetDivergenceQuery(
    {
      search: "",
      conciliation_store_id: conciliation_id as string,
      justification: filters?.justification ?? "",
      divergence: filters?.divergence ?? "",
      dates: filters?.dates ?? "",
      meta: { page: filters?.page ?? 1 },
    },
    {
      refetchOnFocus: false,
      refetchOnMountOrArgChange: true,
    }
  );

  const { data: conciliation } = useGetOneStoreConciliationQuery(
    {
      idTransaction: conciliation_id as string,
    },
    {
      refetchOnFocus: false,
      refetchOnMountOrArgChange: true,
    }
  );

  const formattedDate = formatDateToBR(conciliation?.date)

  const { data: dtefData } = useGetDTEFPaymentValuesQuery(
    { store_code: conciliation?.store?.code || "", date_operation: formattedDate },
    { skip: !conciliation?.store?.code || !formattedDate, refetchOnMountOrArgChange: true }
  );

  const divergences: IDivergence[] = useMemo(
    () => response?.data ?? [],
    [response?.data]
  );

  const handleRefreshAll = useCallback(() => {
    dispatch(apiSlice.util.invalidateTags([Tags.DIVERGENCE, Tags.STORE_CONCILIATION, Tags.DTEF]));
  }, [dispatch]);

  if (isLoading) {
    return <Load />;
  }

  return (
    <ReprocessProvider conciliationId={parseInt(conciliation_id as string, 10)}>
      <PageLayout>
        <div className={layoutClasses.pageContainer}>
          {openCashRegisters.hasAny && (
            <OpenCashRegistersBanner codes={openCashRegisters.codes} />
          )}
          <DivergenceList
            divergences={divergences}
            isLoading={isLoading}
            isFetching={isFetching}
            conciliation={conciliation}
            conciliation_id={conciliation_id}
            onRefresh={handleRefreshAll}
            openCashRegisterCodes={openCashRegisters.codes}
          />
          <div className="flex flex-col lg:flex-row gap-4 min-w-0">
            <TransactionResume
              setIsCollapsibleOpen={setIsCollapsibleOpen}
              conciliation={conciliation}
              dtefData={dtefData}
            />
            <DivergenceRecorded
              isCollapsibleOpen={isCollapsibleOpen}
              divergences={divergences}
              isLoading={isLoading}
              isFetching={isFetching}
              hasOpenCashRegister={openCashRegisters.hasAny}
            />
          </div>
        </div>
        <ReprocessCompletedModal />
        <ReprocessFloatingCard />
      </PageLayout>
    </ReprocessProvider>
  );
}
